import type { ProjectDetail, VersionContent } from '@/stores/project';
import { ProjectStatus, useProjectStore } from '@/stores/project';

interface WebsiteGenerationState {
  status: ProjectStatus;
  currentProjectDetail: Pick<ProjectDetail, 'status'> | null;
  versions: Pick<Map<string, VersionContent>, 'size'>;
  previewVersion: Pick<VersionContent, 'previewUrl'> | null;
  lastVersionId: string;
}

export function hasWebsiteGenerationStarted(state: WebsiteGenerationState): boolean {
  const projectStatusList = [state.status, state.currentProjectDetail?.status];
  const hasStartedStatus = projectStatusList.some(
    status => status === ProjectStatus.GENERATING || status === ProjectStatus.GENERATED,
  );

  return (
    hasStartedStatus ||
    Boolean(state.previewVersion?.previewUrl) ||
    Boolean(state.lastVersionId) ||
    state.versions.size > 0
  );
}

export function hasExistingWebsiteArtifact(state: WebsiteGenerationState): boolean {
  const projectStatusList = [state.status, state.currentProjectDetail?.status];
  return projectStatusList.includes(ProjectStatus.GENERATED)
    || Boolean(state.lastVersionId)
    || state.versions.size > 0;
}

export function hasGeneratedWebsiteArtifact(state: WebsiteGenerationState): boolean {
  const projectStatusList = [state.status, state.currentProjectDetail?.status];
  const hasGeneratedStatus = projectStatusList.includes(ProjectStatus.GENERATED);
  const hasWebsiteGenerationStatus = projectStatusList.some(
    status => status === ProjectStatus.GENERATING || status === ProjectStatus.GENERATED,
  );
  const hasVersionArtifact =
    Boolean(state.previewVersion?.previewUrl) ||
    Boolean(state.lastVersionId) ||
    state.versions.size > 0;

  return hasGeneratedStatus || (hasWebsiteGenerationStatus && hasVersionArtifact);
}

export function getHasWebsiteGenerationStarted(): boolean {
  return hasWebsiteGenerationStarted(useProjectStore.getState());
}

export function useHasWebsiteGenerationStarted(): boolean {
  const status = useProjectStore(state => state.status);
  const currentProjectStatus = useProjectStore(state => state.currentProjectDetail?.status);
  const previewUrl = useProjectStore(state => state.previewVersion?.previewUrl);
  const lastVersionId = useProjectStore(state => state.lastVersionId);
  const versionsSize = useProjectStore(state => state.versions.size);

  return hasWebsiteGenerationStarted({
    status,
    currentProjectDetail: currentProjectStatus ? { status: currentProjectStatus } : null,
    previewVersion: previewUrl ? { previewUrl } : null,
    lastVersionId,
    versions: { size: versionsSize },
  });
}

export function useHasGeneratedWebsiteArtifact(): boolean {
  const status = useProjectStore(state => state.status);
  const currentProjectStatus = useProjectStore(state => state.currentProjectDetail?.status);
  const previewUrl = useProjectStore(state => state.previewVersion?.previewUrl);
  const lastVersionId = useProjectStore(state => state.lastVersionId);
  const versionsSize = useProjectStore(state => state.versions.size);

  return hasGeneratedWebsiteArtifact({
    status,
    currentProjectDetail: currentProjectStatus ? { status: currentProjectStatus } : null,
    previewVersion: previewUrl ? { previewUrl } : null,
    lastVersionId,
    versions: { size: versionsSize },
  });
}
